import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError, apiClient } from '../../core/api';
import type { ApiUser, LedgerEntry, RegistrationResult, WalletSnapshot } from '../../core/api';
import type { AuthMode } from './authMode';
import { loadEntitlements } from '../subscription/entitlements';

type PendingVerification = RegistrationResult & { email: string };

function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 401;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : '账户操作失败';
}

export function useAccount(initialMode: AuthMode = 'login') {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[] | null>(null);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pendingVerification, setPendingVerification] = useState<PendingVerification | null>(null);
  const [code, setCode] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  const epoch = useRef(0);
  const running = useRef(false);

  const isCurrent = useCallback((version: number) => mounted.current && epoch.current === version, []);
  const clearSession = useCallback(() => {
    setUser(null);
    setWallet(null);
    setLedger(null);
    setPassword('');
  }, []);

  const run = useCallback(async (operation: (version: number) => Promise<void>) => {
    if (!mounted.current || running.current) return;
    running.current = true;
    const version = ++epoch.current;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await operation(version);
    } catch (requestError) {
      if (isCurrent(version)) setError(errorText(requestError));
    } finally {
      if (isCurrent(version)) {
        running.current = false;
        setBusy(false);
      }
    }
  }, [isCurrent]);

  const loadBalances = useCallback(async (version: number) => {
    const [nextWallet, nextLedger] = await Promise.allSettled([apiClient.wallet(), apiClient.ledger()]);
    if (!isCurrent(version)) return;
    if ([nextWallet, nextLedger].some((result) => result.status === 'rejected' && isUnauthorized(result.reason))) {
      clearSession();
      setMessage(null);
      setError('登录已失效，请重新登录');
      return;
    }
    setWallet(nextWallet.status === 'fulfilled' ? nextWallet.value : null);
    setLedger(nextLedger.status === 'fulfilled' ? nextLedger.value.filter((entry) => entry.type !== 'reservation' && entry.type !== 'refund') : null);
    const failures = [nextWallet, nextLedger].flatMap((result) => result.status === 'rejected' ? [errorText(result.reason)] : []);
    if (failures.length > 0) setError(failures.join('；'));
  }, [clearSession, isCurrent]);

  const reload = useCallback(() => run(async (version) => {
    let current: ApiUser;
    try {
      current = await apiClient.currentUser();
    } catch (requestError) {
      if (isCurrent(version) && isUnauthorized(requestError)) {
        clearSession();
        return;
      }
      throw requestError;
    }
    if (!isCurrent(version)) return;
    setUser(current);
    setWallet(null);
    setLedger(null);
    await loadBalances(version);
  }), [clearSession, isCurrent, loadBalances, run]);

  const invalidate = useCallback(() => {
    mounted.current = false;
    ++epoch.current;
    running.current = false;
  }, []);

  useEffect(() => {
    mounted.current = true;
    void reload();
    return invalidate;
  }, [invalidate, reload]);

  function submit(): Promise<void> {
    return run(async (version) => {
      if (mode === 'register') {
        const result = await apiClient.register(email.trim(), password);
        if (!isCurrent(version)) return;
        clearSession();
        setCode('');
        if (result.verificationRequired) {
          setPendingVerification({ ...result, email: email.trim() });
          setMessage('账户已创建，请完成邮箱验证后登录');
        } else {
          setMode('login');
          setMessage('账户已创建，请登录');
        }
        return;
      }
      const result = await apiClient.login(email.trim(), password);
      if (!isCurrent(version)) return;
      setUser(result.user);
      setPassword('');
      setWallet(null);
      setLedger(null);
      setMessage('登录成功');
      await loadBalances(version);
      if (isCurrent(version)) await loadEntitlements(true);
    });
  }

  function verifyEmail(): Promise<void> {
    return run(async (version) => {
      if (pendingVerification === null || !/^\d{6}$/.test(code)) throw new Error('请输入六位数字验证码');
      await apiClient.verifyEmail(pendingVerification.userId, code);
      if (!isCurrent(version)) return;
      setEmail(pendingVerification.email);
      setPendingVerification(null);
      setCode('');
      setPassword('');
      setMode('login');
      setMessage('邮箱验证成功，请登录');
    });
  }

  function requestReset(): Promise<void> {
    return run(async (version) => {
      await apiClient.requestPasswordReset(email.trim());
      if (!isCurrent(version)) return;
      setResetRequested(true);
      setMessage('如果该邮箱对应有效账户，重置验证码将发送到邮箱。');
    });
  }

  function confirmReset(): Promise<void> {
    return run(async (version) => {
      if (!/^\d{6}$/.test(code)) throw new Error('请输入六位数字验证码');
      await apiClient.confirmPasswordReset(email.trim(), code, password);
      if (!isCurrent(version)) return;
      setPassword(''); setCode(''); setResetMode(false); setResetRequested(false);
      setMode('login'); setMessage('密码已重置，请使用新密码登录');
    });
  }

  function logout(): Promise<void> {
    return run(async (version) => {
      await apiClient.logout();
      if (!isCurrent(version)) return;
      clearSession();
      setPendingVerification(null);
      setCode('');
      setMode('login');
      setMessage('已退出登录');
    });
  }

  const sessionExpired = useCallback(() => {
    ++epoch.current;
    running.current = false;
    clearSession(); setBusy(false); setMessage(null); setError('登录已失效，请重新登录');
  }, [clearSession]);

  return {
    user, wallet, ledger, mode, email, password, pendingVerification, code, resetMode, resetRequested, busy, message, error,
    setEmail, setPassword, setCode, submit, verifyEmail, requestReset, confirmReset, logout, reload, sessionExpired,
    toggleResetMode: () => {
      if (running.current) return;
      setResetMode(value => !value); setResetRequested(false); setCode(''); setPassword(''); setError(null); setMessage(null);
    },
    switchMode: () => {
      if (running.current) return;
      setMode((current) => current === 'login' ? 'register' : 'login');
      setPassword('');
      setError(null);
      setMessage(null);
    },
    cancelVerification: () => {
      if (running.current) return;
      setPendingVerification(null);
      setCode('');
      setMode('login');
      setError(null);
      setMessage(null);
    },
  };
}
